import { NSARouterMakerResult, NSABoxSecret } from "namirasoft-account-react";
import { SshAuthType, SSHCreateRow, SshRow } from "namirasoft-credential";
import { NSBox, NSBoxEnum, NSBoxInteger, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { CredentialEntities } from "../CredentialEntities";
import { CredentialLayout } from '../CredentialLayout';
import { CredentialProps } from "../CredentialProps";

interface SSHEditPageProps extends NSARouterMakerResult, CredentialProps
{
    isEdit: boolean;
}

interface SSHEditPageState
{
    type?: SshAuthType;
}

export default class SSHEditPage extends Component<SSHEditPageProps, SSHEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxEnum_AuthType = createRef<NSBoxEnum<SshAuthType>>();
    NSABoxSecret_UsernameSecretID = createRef<NSABoxSecret>();
    NSABoxSecret_PasswordSecretID = createRef<NSABoxSecret>();
    NSBoxString_Host = createRef<NSBoxString>();
    NSBoxInteger_Port = createRef<NSBoxInteger>();
    NSABoxSecret_PrivateKey = createRef<NSABoxSecret>();
    NSABoxSecret_PrivateKeyPassphrase = createRef<NSABoxSecret>();
    NSABoxSecret_Fingerprint = createRef<NSABoxSecret>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: SSHEditPageProps)
    {
        super(props);
        this.state = {};
    }

    override render()
    {
        return (
            <CredentialLayout<SshRow, SSHCreateRow>
                {...this.props}
                action={{
                    description: this.props.isEdit ? "This section allows you to edit an SSH credential by updating its name, private key, or description. The private key is kept as a protected secret, and any application set up to use the credential applies the updated value." : "This section allows you to create an SSH credential that stores a private key for secure connections to servers and to Git over SSH by entering a name and the private key. Namirasoft encrypts the sensitive data for secure access, and any Namirasoft application you set up to use it can then authenticate SSH sessions."
                }}
                pages={{
                    entity: CredentialEntities.ssh(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): SSHCreateRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let host = this.NSBoxString_Host.current?.getValue() ?? "";
                                        let port = this.NSBoxInteger_Port.current?.getValue() ?? 0;
                                        let auth_type = this.NSBoxEnum_AuthType.current?.getValueEnumOne(SshAuthType, SshAuthType.Password) ?? SshAuthType.Password;
                                        let username = this.NSABoxSecret_UsernameSecretID.current?.getValue() ?? null;
                                        let password = this.NSABoxSecret_PasswordSecretID.current?.getValue() ?? null;
                                        let private_key = this.NSABoxSecret_PrivateKey.current?.getValue() ?? null;
                                        let private_key_passphrase = this.NSABoxSecret_PrivateKeyPassphrase.current?.getValue() ?? null;
                                        let fingerprint = this.NSABoxSecret_Fingerprint.current?.getValue() ?? null;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? null;

                                        return {
                                            workspace_id: "",
                                            name,
                                            auth_type,
                                            host,
                                            port,
                                            username,
                                            password,
                                            description,
                                            private_key,
                                            private_key_passphrase,
                                            fingerprint,
                                            ssh_category: [],
                                            ssh_field: [],
                                            ssh_tag: [],
                                        };
                                    },
                                    setRow: (row: SshRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxString_Host.current?.setValue(row.host);
                                        this.NSBoxInteger_Port.current?.setValue(row.port);
                                        this.NSBoxEnum_AuthType.current?.setValue(row.auth_type);
                                        this.setState(() => ({ type: row.auth_type }), () =>
                                        {
                                            this.NSABoxSecret_UsernameSecretID.current?.setValue(row.username_secret_id);
                                            this.NSABoxSecret_PasswordSecretID.current?.setValue(row.password_secret_id);
                                            this.NSABoxSecret_PrivateKey.current?.setValue(row.private_key_secret_id);
                                            this.NSABoxSecret_PrivateKeyPassphrase.current?.setValue(row.private_key_passphrase_secret_id);
                                            this.NSABoxSecret_Fingerprint.current?.setValue(row.fingerprint_secret_id);
                                        });
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);
                                    }
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            required
                            title="Name"
                            auto_complete="new-password"
                        />
                        <NSBoxEnum<SshAuthType>
                            ref={this.NSBoxEnum_AuthType}
                            title="Type"
                            required
                            multiple={false}
                            getEnumObject={() => SshAuthType}
                            onChanged={(box) => this.setState(() => ({ type: box.getValueOne() as SshAuthType }))}
                        />
                    </NSRow>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Host}
                            required={false}
                            title="Host"
                        />
                        <NSBoxInteger
                            ref={this.NSBoxInteger_Port}
                            required={false}
                            title="Port"
                        />
                    </NSRow>
                    {
                        this.state.type === SshAuthType.Password &&
                        <>
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_UsernameSecretID}
                                title="Username"
                                required
                                style={{ width: NSBox.width.double }}
                            />
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_PasswordSecretID}
                                title="Password"
                                required
                                style={{ width: NSBox.width.double }}
                            />
                        </>
                    }
                    {
                        this.state.type === SshAuthType.PrivateKey &&
                        <>
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_PrivateKey}
                                title="Private Key"
                                required
                                style={{ width: NSBox.width.double }}
                            />
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_PrivateKeyPassphrase}
                                title="Private Key Passphrase"
                                required
                                style={{ width: NSBox.width.double }}
                            />
                            <NSABoxSecret
                                {...this.props}
                                ref={this.NSABoxSecret_Fingerprint}
                                title="Fingerprint"
                                required
                                style={{ width: NSBox.width.double }}
                            />
                        </>
                    }
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        required={false}
                        title="Description"
                        style={{ width: NSBox.width.double }}
                    />
                </NSColumn>
            </CredentialLayout>
        );
    }
}